import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Flex, Box } from '@televet/kibble-ui';
import {
  AutomationsSettings,
  AutomationsCompose,
  AutomationsHeader,
  AutomationsFooter,
  AutomationsEmptyState,
} from 'pages/Automations/components';
import { AutomationEditorView } from 'pages/Automations/types/AutomationEditorView';
import { useAutomationEditorView } from 'pages/Automations/hooks/useAutomationEditorView';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { AutomationsLoadingState } from '../Layout/AutomationsLoadingState';
import { useParams } from 'react-router-dom';
import { AutomationsErrorAlert } from '../Layout/AutomationsErrorAlert';
import { getFlattenedAutomationErrors } from 'pages/Automations/utils/validation/getFlattenedAutomationErrors';

interface AutomationEditorProps {
  isLoading: boolean;
  isEmpty: boolean;
}

export const AutomationEditor = ({ isLoading, isEmpty }: AutomationEditorProps): JSX.Element => {
  const { currentEditorView } = useAutomationEditorView();
  const { workflowEventSettingId: currentAutomationId } = useParams<{ workflowEventSettingId?: string }>();
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();
  const isErrorAlertVisible = useAppSelector((state) => state.automations.isErrorAlertVisible);
  const errors = useAppSelector((state) => state.automations.errors);

  const flattenedErrors = useMemo(() => {
    return getFlattenedAutomationErrors(errors);
  }, [errors]);

  const scrollToTop = useCallback(() => {
    contentContainerRef?.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const automationHasErrors = useMemo(() => {
    return Boolean(flattenedErrors.generalErrors.length || flattenedErrors.promptErrors?.length) || false;
  }, [flattenedErrors]);

  useEffect(() => {
    if (currentAutomationId || currentEditorView) {
      scrollToTop();
    }
  }, [dispatch, scrollToTop, currentAutomationId, currentEditorView]);

  return (
    <Flex
      className="Automations__Body"
      bg="background.default"
      height="100%"
      flexBasis="100%"
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {isLoading && <AutomationsLoadingState />}
      {isEmpty ? (
        <AutomationsEmptyState />
      ) : (
        <Box width="100%" height="100%" position="relative" overflow="hidden">
          <AutomationsHeader />
          {automationHasErrors && isErrorAlertVisible && <AutomationsErrorAlert errors={flattenedErrors} />}
          <Box
            height="100%"
            className="Automations__Content"
            pt="58px"
            pb="360px"
            px={10}
            ref={contentContainerRef}
            overflowX="hidden"
            overflowY="scroll"
          >
            {currentEditorView === AutomationEditorView.Settings && (
              <Box pt={automationHasErrors ? '30px' : 0} display="flex" justifyContent="center" flex="1 1 auto">
                <AutomationsSettings />
              </Box>
            )}
            {currentEditorView === AutomationEditorView.Compose && (
              <Box pt={automationHasErrors ? '30px' : 0} display="flex" justifyContent="center" flex="1 1 auto">
                <AutomationsCompose />
              </Box>
            )}
          </Box>
          <AutomationsFooter />
        </Box>
      )}
    </Flex>
  );
};
